import { msg } from '@lit/localize';
import { DataFrame } from '../model/DataFrame';
import writeXlsxFile, {} from 'write-excel-file/browser';
export function poisListToGeoJson(poiList, light = true) {
    const pois_with_coords = poiList.filter((poi) => poi.latitude !== null && poi.longitude !== null);
    if (pois_with_coords.length === 0) {
        return {
            type: 'FeatureCollection',
            features: [],
        };
    }
    const features = pois_with_coords.map((poi) => ({
        type: 'Feature',
        geometry: {
            type: 'Point',
            coordinates: [poi.longitude, poi.latitude],
        },
        properties: light ? {
            id: poi.id,
            name: poi.name,
            description: poi.description
        } : {
            ...poi,
            properties: null,
            ...poi.properties,
        },
    }));
    return {
        type: 'FeatureCollection',
        features,
        bbox: [
            Math.min(...features.map(f => f.geometry.coordinates[0]).filter((v) => v !== undefined)),
            Math.min(...features.map(f => f.geometry.coordinates[1]).filter((v) => v !== undefined)),
            Math.max(...features.map(f => f.geometry.coordinates[0]).filter((v) => v !== undefined)),
            Math.max(...features.map(f => f.geometry.coordinates[1]).filter((v) => v !== undefined)),
        ],
    };
}
function getSvgStatus(poi) {
    if (poi.geocoder_accuracy && poi.geocoder_accuracy < 0.6) {
        return '<img src="' + new URL('../assets/error.svg', import.meta.url).href + '" alt="error" title="' + msg('Low geocoding accuracy', { id: 'poi.status.lowAccuracy', desc: 'POI geocoding status: low accuracy' }) + '" style="width:20px; height:20px; margin-right: 8px;">';
    }
    if (!poi.longitude || !poi.latitude) {
        return '<img src="' + new URL('../assets/incorrect.svg', import.meta.url).href + '" alt="incorrect" title="' + msg('Missing or incorrect coordinates', { id: 'poi.status.missingCoords', desc: 'POI geocoding status: missing or incorrect coordinates' }) + '" style="width:20px; height:20px; margin-right: 8px;">';
    }
    return '<img src="' + new URL('../assets/correct.svg', import.meta.url).href + '" alt="correct" title="' + msg('POI is correctly geocoded', { id: 'poi.status.correct', desc: 'POI geocoding status: correctly geocoded' }) + '" style="width:20px; height:20px; margin-right: 8px;">';
}
export function getDataFrameFromPois(listPois, listNetworkFields) {
    let columns = [];
    columns.push({ name: 'poi_id', alias: 'POI ID', type: 'number', id: true }); // Unique identifier
    columns.push({ name: '_status', alias: 'Status', type: 'html', id: false }); // Status column
    // Define columns based on network fields
    listNetworkFields.sort((a, b) => (a.index || 0) - (b.index || 0));
    listNetworkFields.filter(f => f.visible).forEach((field) => {
        columns.push({ name: field.name, alias: field.alias || field.name, type: field.type, id: false });
    });
    // Populate rows with POI data
    let rows = listPois.map((poi) => {
        let row = {};
        listNetworkFields.forEach((field) => {
            if (field.native && field.name in poi) {
                row[field.name] = poi[field.name]; // Cast explicite
            }
            else {
                if (poi.properties && field.name in poi.properties) {
                    row[field.name] = poi.properties[field.name];
                }
                else {
                    row[field.name] = null; // or some default value
                }
            }
        });
        row['poi_id'] = poi.poi_id;
        row['_status'] = getSvgStatus(poi);
        return row;
    });
    return new DataFrame(columns, rows);
}
function escapeCsvValue(value) {
    if (value === null || value === undefined) {
        return '';
    }
    const stringValue = String(value);
    if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n') || stringValue.includes('\r') || stringValue !== stringValue.trim()) {
        return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
}
export function exportPoisCSV(pois) {
    if (pois.length === 0) {
        return '';
    }
    const columns = [
        'id',
        'name',
        'description',
        'address',
        'postal_code',
        'city',
        'open_date',
        'geocoder_accuracy',
        'geounit_code',
        'logo_id',
        'latitude',
        'longitude',
        'status',
        // Add other properties as needed
    ];
    // get all unique property keys from pois
    const propertyKeys = new Set();
    pois.forEach(poi => {
        if (poi.properties) {
            Object.keys(poi.properties).forEach(key => propertyKeys.add(key));
        }
    });
    columns.push(...Array.from(propertyKeys));
    // Create CSV header
    const header = columns.join(',') + '\n';
    // Create CSV rows
    const rows = pois.map(poi => {
        const row = columns.map(col => {
            let value;
            if (col in poi) {
                value = poi[col]; // Cast explicite
            }
            else if (poi.properties && col in poi.properties) {
                value = poi.properties[col];
            }
            else {
                value = '';
            }
            return escapeCsvValue(value);
        }).join(',');
        return row;
    }).join('\n');
    return header + rows;
}
export function exportPois(pois, format) {
    if (format === 'geojson') {
        const geojson = poisListToGeoJson(pois, false);
        return JSON.stringify(geojson);
    }
    else if (format === 'csv') {
        return exportPoisCSV(pois);
    }
    else {
        // error handling for unsupported formats
        throw new Error(`Unsupported export format: ${format}`);
    }
}
export function poisCreateFromDataFrame(dataFrame, networkFields) {
    const pois = dataFrame.rows.map((row) => {
        const poi = {};
        networkFields.forEach((field) => {
            try {
                if (field.native) {
                    if (field.type === 'number') {
                        poi[field.name] = Number(row[field.name]) || null;
                    }
                    else if (field.type === 'boolean') {
                        poi[field.name] = Boolean(row[field.name]) || null;
                    }
                    else if (field.type === 'date') {
                        const dateValue = convertToDate(row[field.name]);
                        poi[field.name] = dateValue ? dateValue.toISOString() : null; // Format ISO 8601
                    }
                    else {
                        poi[field.name] = row[field.name]?.toString();
                    }
                }
                else {
                    if (!poi.properties) {
                        poi.properties = {};
                    }
                    if (field.type === 'number') {
                        poi.properties[field.name] = Number(row[field.name]) || null;
                    }
                    else if (field.type === 'boolean') {
                        poi.properties[field.name] = Boolean(row[field.name]) || null;
                    }
                    else if (field.type === 'date') {
                        const dateValue = convertToDate(row[field.name]) || null;
                        poi.properties[field.name] = dateValue ? dateValue.toISOString() : null; // Format ISO 8601
                    }
                    else {
                        poi.properties[field.name] = row[field.name]?.toString();
                    }
                }
            }
            catch {
                if (field.native) {
                    poi[field.name] = null;
                }
                else {
                    if (!poi.properties) {
                        poi.properties = {};
                    }
                    poi.properties[field.name] = null;
                }
            }
        });
        return poi;
    });
    return pois;
}
function convertToDate(value) {
    if (!value) {
        return null;
    }
    // Si la valeur est déjà un objet Date valide
    if (value instanceof Date && !isNaN(value.getTime())) {
        return value;
    }
    // Si la valeur est un timestamp (nombre)
    if (typeof value === 'number') {
        const date = new Date(value);
        return isNaN(date.getTime()) ? null : date;
    }
    // Si la valeur est une chaîne de caractères
    if (typeof value === 'string') {
        // Essayer de convertir directement
        const directDate = new Date(value);
        if (!isNaN(directDate.getTime())) {
            return directDate;
        }
        // Essayer des formats spécifiques
        const formats = [
            /^\d{4}-\d{2}-\d{2}$/, // YYYY-MM-DD
            /^\d{4}\/\d{2}\/\d{2}$/, // YYYY/MM/DD
            /^\d{2}-\d{2}-\d{4}$/, // DD-MM-YYYY
            /^\d{2}\/\d{2}\/\d{4}$/, // DD/MM/YYYY
            /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z?$/, // ISO 8601
        ];
        for (const format of formats) {
            if (format.test(value)) {
                const parsedDate = new Date(value.replace(/-/g, '/')); // Remplacer '-' par '/' si nécessaire
                if (!isNaN(parsedDate.getTime())) {
                    return parsedDate;
                }
            }
        }
    }
    // Si aucune conversion n'a fonctionné
    return null;
}
function getTextStatus(poi) {
    if (poi.geocoder_accuracy && poi.geocoder_accuracy < 0.6) {
        return msg('Low geocoding accuracy', { id: 'poi.status.lowAccuracy', desc: 'POI geocoding status: low accuracy' });
    }
    if (!poi.longitude || !poi.latitude) {
        return msg('Missing or incorrect coordinates', { id: 'poi.status.missingCoords', desc: 'POI geocoding status: missing or incorrect coordinates' });
    }
    return msg('POI is correctly geocoded', { id: 'poi.status.correct', desc: 'POI geocoding status: correctly geocoded' });
}
function triggerDownload(blob, fileName) {
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(link.href);
}
/**
 * Per-locale export settings.
 *
 * `numberFormat` is an **xlsx format code, not a display string**. In a
 * `numFmt`, `,` is the thousands-grouping placeholder and `.` the decimal
 * placeholder whatever the locale; anything else — a space, a literal dot — is
 * emitted verbatim. Excel then renders the groups and the decimal point with
 * the separators of whoever opens the file. So the same canonical
 * `#,##0.00` is correct for every locale: it shows as `1 234 567,89` in a
 * French Excel and `1,234,567.89` in an English one.
 *
 * Writing the display form instead (`# ##0.00`, `#.##0,00`, …) is what this
 * table used to do, and it grouped only the last three digits — `1234 567,89`
 * — or dropped the decimals entirely. `poi.test.ts` guards against it.
 *
 * `dateFormat` is the opposite case: date patterns are literal and Excel does
 * not re-localise them, so they legitimately differ per locale.
 */
export const localesConfig = {
    "fr-FR": {
        name: "Français",
        decimalSeparator: ",",
        thousandsSeparator: " ",
        listSeparator: ";",
        dateFormat: "dd/mm/yyyy",
        numberFormat: "#,##0.00",
    },
    "en-US": {
        name: "English",
        decimalSeparator: ".",
        thousandsSeparator: ",",
        listSeparator: ",",
        dateFormat: "mm/dd/yyyy",
        numberFormat: "#,##0.00",
    },
    "de-DE": {
        name: "Deutsch",
        decimalSeparator: ",",
        thousandsSeparator: ".",
        listSeparator: ";",
        dateFormat: "dd.mm.yyyy",
        numberFormat: "#,##0.00",
    },
    "es-ES": {
        name: "Español",
        decimalSeparator: ",",
        thousandsSeparator: ".",
        listSeparator: ";",
        dateFormat: "dd/mm/yyyy",
        numberFormat: "#,##0.00",
    },
    "it-IT": {
        name: "Italiano",
        decimalSeparator: ",",
        thousandsSeparator: ".",
        listSeparator: ";",
        dateFormat: "dd/mm/yyyy",
        numberFormat: "#,##0.00",
    },
    "pt-PT": {
        name: "Português",
        decimalSeparator: ",",
        thousandsSeparator: " ",
        listSeparator: ";",
        dateFormat: "dd/mm/yyyy",
        numberFormat: "#,##0.00",
    },
};
/** Never narrower than this — a one-character header still needs a usable column. */
const MIN_COLUMN_WIDTH = 10;
/** Never wider than this — a long free-text field must not push the rest off-screen. */
const MAX_COLUMN_WIDTH = 40;
/**
 * How many characters a number takes once `#,##0.00` has been applied: its
 * integer digits, one separator per group of three, the sign, and `,00`.
 */
function formattedNumberLength(value) {
    const digits = Math.floor(Math.abs(value)).toString().length;
    const groupSeparators = Math.floor((digits - 1) / 3);
    const sign = value < 0 ? 1 : 0;
    return digits + groupSeparators + sign + 3;
}
/**
 * Column widths, in characters, wide enough for the header and the widest
 * value below it.
 *
 * Without them Excel falls back to its default width and anything longer —
 * an amount past 8 digits, a long store name — renders as `##########` until
 * the reader widens the column by hand.
 */
export function columnWidths(header, rows) {
    return header.map((title, index) => {
        let longest = String(title).length;
        for (const row of rows) {
            const value = row[index]?.value;
            const length = value instanceof Date
                ? 10 // dd/mm/yyyy
                : typeof value === 'number'
                    ? formattedNumberLength(value)
                    : String(value ?? '').length;
            if (length > longest) {
                longest = length;
            }
        }
        return Math.min(Math.max(longest + 2, MIN_COLUMN_WIDTH), MAX_COLUMN_WIDTH);
    });
}
export async function exportPoisExcel(pois, networkFields, networkName, locale = 'fr-FR') {
    const currentLocale = localesConfig[locale] || localesConfig['fr-FR'];
    let headers = networkFields
        .sort((a, b) => (a.index || 0) - (b.index || 0))
        .map(field => field.name);
    const fieldsToAdd = ['latitude', 'longitude', 'geocoder_accuracy'];
    fieldsToAdd.forEach(field => {
        if (!headers.includes(field)) {
            headers.push(field);
        }
    });
    headers.unshift('status');
    const dataRows = pois.map(poi => {
        return headers.map(header => {
            let rawValue;
            if (header === 'status') {
                rawValue = getTextStatus(poi);
            }
            else if (Object.prototype.hasOwnProperty.call(poi, header)) {
                rawValue = poi[header];
            }
            else if (poi.properties && Object.prototype.hasOwnProperty.call(poi.properties, header)) {
                rawValue = poi.properties[header];
            }
            else {
                rawValue = '';
            }
            if (rawValue === null || rawValue === undefined || rawValue === '') {
                return null;
            }
            const field = networkFields.find(f => f.name === header);
            const fieldType = field ? field.type : (typeof rawValue === 'number' ? 'number' : 'string');
            if (fieldType === 'date') {
                const date = convertToDate(rawValue);
                if (date) {
                    return { value: date, type: Date, format: currentLocale.dateFormat };
                }
            }
            if (fieldType === 'number') {
                const num = Number(String(rawValue).replace(',', '.'));
                if (!isNaN(num)) {
                    return { value: num, type: Number, format: currentLocale.numberFormat };
                }
            }
            return { value: String(rawValue), type: String };
        });
    });
    const headerAliases = headers.map(headerName => {
        if (headerName === 'status')
            return 'Status';
        const field = networkFields.find(f => f.name === headerName);
        return field?.alias || headerName;
    });
    const dataForSheet = [
        headerAliases.map(alias => ({ value: alias, type: String })),
        ...dataRows,
    ];
    const blob = await writeXlsxFile(dataForSheet, {
        sheet: 'POIs',
        columns: columnWidths(headerAliases, dataRows).map((width) => ({ width })),
    }).toBlob();
    triggerDownload(blob, `${networkName}_export.xlsx`);
}
//# sourceMappingURL=poi.js.map